import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { strings } from '@constants'
import { generateCranReportApi, getAllProbeReports, getInspectionReports } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Select } from '@baseV2'

import { InputsWrapper } from '../styles'

// Financial-year label for a probe report (mirrors the FinancialReports list column).
const fyLabel = (report) => {
	const fy = [report?.financial_year1, report?.financial_year2, report?.financial_year3].filter(Boolean)
	if (fy.length) return fy.join(' - ')
	if (report?.created_at) return String(new Date(report.created_at).getFullYear())
	return report?.probe42_id ?? report?.id
}

const deriveFormState = () => ({
	inspection_report_id: {
		id: 'generate-cran-inspection-lead',
		label: strings(['select', 'inspection', 'lead']),
		placeholder: strings(['select', 'inspection', 'lead']),
		value: '',
		required: true,
	},
	probe_id: {
		id: 'generate-cran-financial-year',
		label: strings(['select', 'financial', 'year']),
		placeholder: strings(['select', 'financial', 'year']),
		value: '',
		required: true,
	},
})

const GenerateCran = ({ orgId, update }) => {
	const dispatch = useDispatch()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))

	const [inspectionLeads, setInspectionLeads] = useState([])
	const [financialYears, setFinancialYears] = useState([])
	const [formState, setFormState] = useState(deriveFormState())

	const updateState = (field, key) => (value) => setFormState((state) => ({ ...state, [field]: { ...state[field], [key]: value } }))

	useEffect(() => {
		if (!orgId) return
		dispatch(showLoader(true))
		Promise.all([getInspectionReports(orgId, 0, 100, 'COMPLETED'), getAllProbeReports(orgId, 0, 100)])
			.then(([leadsRes, probeRes]) => {
				const leads = leadsRes?.data ?? leadsRes?.content ?? []
				setInspectionLeads(
					leads.map((item) => ({
						...item,
						display_name: item?.product_name ? `${item.product_name} (${item.id})` : item?.id,
					}))
				)
				// getAllProbeReports resolves a pageable object → { data, count }; fall back to raw `content`.
				const probes = probeRes?.data ?? probeRes?.content ?? []
				setFinancialYears(probes.map((report) => ({ ...report, display_name: fyLabel(report) })))
			})
			.catch(() => dispatch(showToast(true, strings('msg_failed_to_fetch'), { type: TOAST_TYPE.ERROR })))
			.finally(() => dispatch(showLoader(false)))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orgId])

	const disabled = !formState.inspection_report_id.value || !formState.probe_id.value

	const createHandler = () => {
		dispatch(showLoader(true))
		generateCranReportApi({
			inspectionReportId: formState.inspection_report_id.value?.id,
			organisationId: orgId,
			probeId: formState.probe_id.value?.probe42_id,
			userId: loggedInUser?.user_id,
		})
			.then(() => {
				dispatch(showToast(true, strings('request_submit_sucess'), { type: TOAST_TYPE.SUCCESS }))
				dispatch(toggleDrawer(false))
				update?.()
			})
			.catch((error) =>
				dispatch(showToast(true, error?.response?.data?.message ?? strings('error_something_went_wrong'), { type: TOAST_TYPE.ERROR }))
			)
			.finally(() => dispatch(showLoader(false)))
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<Select
						id={formState.inspection_report_id.id}
						label={formState.inspection_report_id.label}
						placeholder={formState.inspection_report_id.placeholder}
						data={inspectionLeads}
						displayKey='display_name'
						primaryKey='id'
						value={formState.inspection_report_id.value ?? {}}
						onChange={updateState('inspection_report_id', 'value')}
					/>
					<Select
						id={formState.probe_id.id}
						label={formState.probe_id.label}
						placeholder={formState.probe_id.placeholder}
						data={financialYears}
						displayKey='display_name'
						primaryKey='probe42_id'
						value={financialYears?.find((item) => item.probe42_id === formState.probe_id.value?.probe42_id) ?? {}}
						onChange={updateState('probe_id', 'value')}
					/>
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' disabled={disabled} onClick={createHandler}>
					{strings('create')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default GenerateCran
